import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChecklistFieldDef, ChecklistTemplateDto, ChecklistTemplateItemDef, DoneRule } from '@tww/shared';
import { CHECKLIST_FIELD_TYPES, DONE_RULE_KINDS, ROLE_NAMES, doneRuleKind } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useCategoryDefs } from '../../api/hooks';
import { ErrorState, Note, Panel, Skeleton } from '../../ui/basics';
import { Checkbox, NumberInput, Select, TextInput } from '../../ui/fields';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';
import { ChecklistField } from '../actions/ChecklistItemModal';
import { slugKey, uniqueKey } from './adminUtil';
import { Dropdown } from '../../ui/Dropdown';

type Item = ChecklistTemplateItemDef & { id: number };
const FIELD_TYPE_LABELS: Record<string, string> = { select: 'Auswahl', date: 'Datum', dates: 'Mehrfachdatum', text: 'Text', number: 'Zahl', textarea: 'Mehrzeiliger Text', contact: 'Kontakt', file: 'Datei', checks: 'Checkboxen mit Datum', reminder: 'Terminfeld' };

/** Verwaltung › Checklisten-Vorlage: Baum Gewerk › Punkt, Felder, Frist, Erledigt-Regel, Vorschau */
export function ChecklistTemplatePage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const write = can('settings', 'write');
  const q = useQuery({ queryKey: ['checklist-templates'], queryFn: () => api.get<{ items: ChecklistTemplateDto[] }>('/checklist-templates').then((r) => r.items) });
  const [tplId, setTplId] = useState<number | null>(null);
  const [sel, setSel] = useState<number | 'new' | null>(null);
  const tpl = q.data?.find((t) => t.id === tplId) ?? q.data?.[0] ?? null;
  useEffect(() => {
    if (tpl && tplId == null) setTplId(tpl.id);
  }, [tpl, tplId]);
  const invalidate = () => qc.invalidateQueries({ queryKey: ['checklist-templates'] });
  const patchItem = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<Item> }) => api.patch(`/checklist-templates/${tpl!.id}/items/${id}`, body),
    onSuccess: () => (invalidate(), toast('Gespeichert')),
    onError: (e: Error) => toast(e instanceof ApiRequestError && e.fields ? Object.values(e.fields).join(', ') : e.message, 'error'),
  });
  const createItem = useMutation({
    mutationFn: (body: Partial<Item>) => api.post(`/checklist-templates/${tpl!.id}/items`, body),
    onSuccess: () => (invalidate(), toast('Punkt angelegt'), setSel(null)),
    onError: (e: Error) => toast(e instanceof ApiRequestError && e.fields ? Object.values(e.fields).join(', ') : e.message, 'error'),
  });
  if (q.isLoading) return <Skeleton rows={8} />;
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />;
  if (!tpl) return <div className="empty">Noch keine Checklisten-Vorlage vorhanden.</div>;
  const item = sel === 'new' ? null : (tpl.items.find((i) => i.id === sel) ?? null);
  return (
    <>
      <Note kind="info">Änderungen an der Vorlage wirken auf neu angelegte Aktionen. Bestehende Aktionen behalten ihre Kopie der Checkliste.</Note>
      <div className="grid g12">
        <div className="col">
          <Panel title="Vorlage">
            <div className="pb">
              <Select label="Vorlage" value={String(tpl.id)} onChange={(v) => (setTplId(Number(v)), setSel(null))} options={q.data!.map((t) => ({ value: String(t.id), label: `${t.name} (${t.action_type_codes.join(', ') || 'Standard'})` }))} placeholder="wählen" />
            </div>
          </Panel>
          <Panel title="Gewerk › Punkt" action={write && <button type="button" className="btn sm" onClick={() => setSel('new')}><Icon name="plus" /> Punkt</button>}>
            <div className="side-list side-list-pad">
              {tpl.groups.map((g) => (
                <div key={g.key}>
                  <div className="mut small" style={{ padding: '10px 12px 4px', fontWeight: 600 }}>
                    {g.name} <span style={{ fontWeight: 400 }}>· {g.responsible_role ? ROLE_NAMES[g.responsible_role] : '–'}{g.after_action ? ' · nach Aktion' : ''}</span>
                  </div>
                  {tpl.items
                    .filter((i) => i.group_key === g.key)
                    .map((i) => (
                      <button type="button" key={i.id} className={`${sel === i.id ? 'on' : ''} ${i.active ? '' : 'row-off'}`} onClick={() => setSel(i.id)}>
                        <span>
                          {i.title}
                          <span className="d">
                            {i.due_offset_days == null ? 'keine Frist' : `${i.due_offset_days > 0 ? '+' : ''}${i.due_offset_days} Tage`}
                            {i.optional ? ' · optional' : ''}
                            {!i.active ? ' · inaktiv' : ''}
                          </span>
                        </span>
                        <span className="n">{i.fields.length} Felder</span>
                      </button>
                    ))}
                </div>
              ))}
            </div>
          </Panel>
        </div>
        <div className="col">
          {sel === 'new' ? (
            <ItemEditor key="new" tpl={tpl} item={null} write={write} busy={createItem.isPending} onSave={(body) => createItem.mutate(body)} onCancel={() => setSel(null)} />
          ) : item ? (
            <ItemEditor key={item.id} tpl={tpl} item={item} write={write} busy={patchItem.isPending} onSave={(body) => patchItem.mutate({ id: item.id, body })} onCancel={() => setSel(null)} />
          ) : (
            <div className="empty">Punkt links wählen oder neu anlegen</div>
          )}
        </div>
      </div>
    </>
  );
}

function ItemEditor({ tpl, item, write, busy, onSave, onCancel }: { tpl: ChecklistTemplateDto; item: Item | null; write: boolean; busy: boolean; onSave: (body: Partial<Item>) => void; onCancel: () => void }) {
  const cats = useCategoryDefs();
  const [f, setF] = useState<Partial<Item>>(item ?? { key: '', title: '', group_key: tpl.groups[0]?.key ?? 'forst', due_offset_days: -14, optional: false, needed_field: null, fields: [{ key: 'ja', label: 'Erledigt', type: 'select', options: ['Ja', 'Nein'] }], done_rule: { f: 'ja', is: ['Ja'] }, active: true, responsible_role: null });
  const [preview, setPreview] = useState<Record<string, unknown>>({});
  const [keyTouched, setKeyTouched] = useState(false);
  const [manualKeys, setManualKeys] = useState<Set<number>>(new Set());
  const set = (p: Partial<Item>) => setF((s) => ({ ...s, ...p }));
  const fields = f.fields ?? [];
  const savedKeys = new Set((item?.fields ?? []).map((x) => x.key));
  const setField = (i: number, p: Partial<ChecklistFieldDef>) => set({ fields: fields.map((x, j) => (j === i ? { ...x, ...p } : x)) });
  /** Feldschlüssel ändern und Verweise in Erledigt-Regel und „Nicht benötigt“-Feld mitziehen */
  const renameField = (i: number, key: string, label?: string) => {
    const old = fields[i]!.key;
    const swap = (k: string) => (k === old ? key : k);
    const r = f.done_rule as DoneRule | undefined;
    const rule: DoneRule | undefined = !r ? r : 'f' in r ? { ...r, f: swap(r.f) } : 'all' in r ? { all: r.all.map(swap) } : 'any' in r ? { any: r.any.map(swap) } : 'anyOr' in r ? { ...r, anyOr: r.anyOr.map(swap) } : 'checks' in r ? { ...r, checks: swap(r.checks) } : r;
    set({ fields: fields.map((x, j) => (j === i ? { ...x, key, ...(label != null ? { label } : {}) } : x)), done_rule: rule, needed_field: f.needed_field === old ? key : f.needed_field });
  };
  const setFieldLabel = (i: number, label: string) => {
    const x = fields[i]!;
    if (savedKeys.has(x.key) || manualKeys.has(i)) return setField(i, { label });
    renameField(i, uniqueKey(slugKey(label), fields.filter((_, j) => j !== i).map((y) => y.key)), label);
  };
  const itemKeys = tpl.items.map((x) => x.key);
  const rule = f.done_rule ?? { never: true };
  const kind = doneRuleKind(rule as DoneRule);
  const ruleFields = 'f' in rule ? [rule.f] : 'all' in rule ? rule.all : 'any' in rule ? rule.any : 'anyOr' in rule ? rule.anyOr : 'checks' in rule ? [rule.checks] : [];
  const setRule = (k: string, refs: string[], values?: string[]) => {
    const r: DoneRule = k === 'f' ? { f: refs[0] ?? '', is: values ?? [] } : k === 'all' ? { all: refs } : k === 'any' ? { any: refs } : k === 'checks' ? { checks: refs[0] ?? '', min: values ?? [] } : { never: true };
    set({ done_rule: r });
  };
  const ruleValues = 'is' in rule ? rule.is : 'min' in rule ? rule.min : [];
  return (
    <>
      <Panel title={item ? `Punkt „${item.title}“` : 'Neuer Punkt'}>
        <div className="pb">
          <div className="form">
            <TextInput label="Titel" required value={f.title ?? ''} onChange={(v) => set({ title: v, key: item || keyTouched ? f.key : uniqueKey(slugKey(v, 40, ''), itemKeys) })} />
            <Select label="Gewerk" value={f.group_key ?? ''} onChange={(v) => set({ group_key: v })} options={tpl.groups.map((g) => ({ value: g.key, label: g.name }))} placeholder="wählen" />
            <NumberInput label="Standardfrist (Tage relativ zum Aktionstag)" value={f.due_offset_days ?? null} onChange={(v) => set({ due_offset_days: v })} hint="negativ = davor, leer = keine Frist" />
            <Select label="Zuständigkeit" value={f.responsible_role ?? ''} onChange={(v) => set({ responsible_role: (v || null) as Item['responsible_role'] })} options={Object.entries(ROLE_NAMES).map(([k, l]) => ({ value: k, label: l }))} placeholder="aus Gewerk" />
            <Select label="„Nicht benötigt“-Feld" value={f.needed_field ?? ''} onChange={(v) => set({ needed_field: v || null })} options={fields.filter((x) => x.type === 'select').map((x) => ({ value: x.key, label: x.label }))} placeholder="keins" hint="Wert „Nein“ oder „nicht benötigt“ setzt den Punkt auf nicht benötigt" />
            <Checkbox value={Boolean(f.optional)} onChange={(v) => set({ optional: v })} label="Optional (zählt nicht im Fortschritt)" />
            <Checkbox value={f.active !== false} onChange={(v) => set({ active: v })} label="Aktiv (wird in neuen Aktionen angelegt)" />
          </div>
          <details className="fs mt-12">
            <summary>Erweitert</summary>
            <div className="body">
              <div className="form one">
                <TextInput label="Schlüssel des Punkts" value={f.key ?? ''} onChange={(v) => (setKeyTouched(true), set({ key: v }))} disabled={Boolean(item)} hint={item ? 'Fest, verbindet den Punkt mit E-Mail-Vorlagen und bestehenden Aktionen' : 'Wird aus dem Titel erzeugt und bleibt nach dem Anlegen fest'} />
                {fields.length > 0 && (
                  <div className="field">
                    <label>Schlüssel der Felder</label>
                    <div className="key-list">
                      {fields.map((x, i) => (
                        <label key={i} className="key-row">
                          <span>{x.label || 'ohne Bezeichnung'}</span>
                          <input
                            className="tbl-input"
                            value={x.key}
                            onChange={(e) => {
                              setManualKeys((s) => new Set(s).add(i));
                              renameField(i, e.target.value);
                            }}
                            disabled={savedKeys.has(x.key) && Boolean(item)}
                            aria-label={`Schlüssel für ${x.label}`}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </details>
        </div>
      </Panel>
      <Panel title="Felder" action={write && <button type="button" className="btn sm" onClick={() => set({ fields: [...fields, { key: uniqueKey('feld', fields.map((y) => y.key)), label: '', type: 'text' }] })}><Icon name="plus" /> Feld</button>}>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>Bezeichnung</th>
                <th>Typ</th>
                <th>Optionen / Kategorie</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {fields.map((x, i) => (
                <tr key={i}>
                  <td>
                    <input value={x.label} onChange={(e) => setFieldLabel(i, e.target.value)} aria-label="Bezeichnung" placeholder="z. B. Erledigt" />
                  </td>
                  <td>
                    <Dropdown value={x.type} onChange={(v) => setField(i, { type: v as ChecklistFieldDef['type'] })} aria-label="Typ" clearable={false} options={CHECKLIST_FIELD_TYPES.map((t) => ({ value: String(t), label: FIELD_TYPE_LABELS[t] }))} />
                  </td>
                  <td>
                    {(x.type === 'select' || x.type === 'checks') && <input value={(x.options ?? []).join(', ')} onChange={(e) => setField(i, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="Option, Option" aria-label="Optionen" />}
                    {x.type === 'contact' && (
                      <span style={{ display: 'flex', gap: 6 }}>
                        <Dropdown value={x.contact_category ?? ''} onChange={(v) => setField(i, { contact_category: v || undefined })} aria-label="Kontaktkategorie" placeholder="Kategorie" options={(cats.data ?? []).map((c) => ({ value: String(c.key), label: c.name }))} />
                        <label className="small" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <input type="checkbox" checked={Boolean(x.multi)} onChange={(e) => setField(i, { multi: e.target.checked })} /> mehrere
                        </label>
                      </span>
                    )}
                    {(x.type === 'dates' || x.type === 'checks') && (
                      <label className="small" style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 4 }}>
                        <input type="checkbox" checked={Boolean(x.file)} onChange={(e) => setField(i, { file: e.target.checked })} /> mit Datei
                      </label>
                    )}
                  </td>
                  <td className="num">
                    <button type="button" className="icon-btn" aria-label="Feld entfernen" onClick={() => set({ fields: fields.filter((_, j) => j !== i) })}>
                      <Icon name="trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
      <Panel title="Erledigt, wenn">
        <div className="pb">
          <div className="form">
            <Select label="Regel" value={kind} onChange={(v) => setRule(v, ruleFields, ruleValues)} options={DONE_RULE_KINDS.map((k) => ({ value: k.kind, label: k.label }))} placeholder="wählen" />
            {kind !== 'never' && (
              <div className="field">
                <label>{kind === 'f' || kind === 'checks' ? 'Feld' : 'Felder'}</label>
                <ul className="check-list no-date">
                  {fields
                    .filter((x) => x.type !== 'reminder')
                    .map((x) => (
                      <li key={x.key} className={ruleFields.includes(x.key) ? 'on' : ''}>
                        <label>
                          <input type={kind === 'f' || kind === 'checks' ? 'radio' : 'checkbox'} name="done-rule-field" checked={ruleFields.includes(x.key)} onChange={(e) => setRule(kind, kind === 'f' || kind === 'checks' ? [x.key] : e.target.checked ? [...ruleFields, x.key] : ruleFields.filter((k) => k !== x.key), ruleValues)} />
                          <span>{x.label}</span>
                        </label>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            {(kind === 'f' || kind === 'checks') && <TextInput label={kind === 'f' ? 'Werte (durch Komma)' : 'Mindestens angehakt (durch Komma)'} value={ruleValues.join(', ')} onChange={(v) => setRule(kind, ruleFields, v.split(',').map((s) => s.trim()).filter(Boolean))} />}
          </div>
        </div>
      </Panel>
      <Panel title="Vorschau der Aufgabenmaske">
        <div className="pb">
          <div className="form one">
            {fields.map((x) => (
              <ChecklistField key={x.key} f={x} value={preview[x.key]} onChange={(v) => setPreview((p) => ({ ...p, [x.key]: v }))} region={null} contacts={[]} onContacts={() => {}} />
            ))}
          </div>
        </div>
      </Panel>
      {write && (
        <div className="btn-row btn-row-end">
          <button type="button" className="btn" onClick={onCancel}>
            Abbrechen
          </button>
          <button type="button" className="btn primary" disabled={busy || !f.title || !f.key || !fields.length} onClick={() => onSave(f)}>
            Speichern
          </button>
        </div>
      )}
    </>
  );
}
